import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import type { DomainStore } from '~/domain/changes/DomainStore';
import { Patient } from '~/domain/patient/patient';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { localization } from '~/i18n';
import { seriesSummaryOf } from '~/utils/transforms/imageDataTransforms';
import {
    BlendSettings,
    DEFAULT_BLEND_SETTINGS,
} from '~/domain/viewport/fusionBlend';
import type {
    DatasetEnvironmentRegistry,
    DatasetRegistryEntry,
} from '~/engine/scene/DatasetEnvironmentRegistry';
import { bindingsOf, ImageBinding } from '~/domain/patient/imageBinding';
import { ChangeNotifier } from '~/viewportGrid/adapters/changeSource';
import {
    BlendAdapter,
    BlendCandidate,
    BlendSource,
} from '~/viewportGrid/adapters/types';
import { pairEndOf, warnFusionRefused } from '../fusionRefusedWarning';

/**
 * The candidate `entry` makes, bound to the row's image by `binding`
 * through `registration` when the binding is mapped. Only a mapped
 * candidate carries its registration's label.
 */
function toBlendCandidate(
    entry: DatasetRegistryEntry,
    binding: ImageBinding,
    registration: RigidRegistration | undefined
): BlendCandidate {
    return {
        ...seriesSummaryOf(entry.image, localization.t('unknown.series')),
        seriesInstanceUid: entry.seriesUid,
        binding,
        registrationLabel:
            binding.kind === 'mapped' ? registration?.contentLabel : undefined,
    };
}

/**
 * Classifies `otherEntry` as a blend candidate for `primaryEntry`, one
 * result per registration a drag on the primary edits (an image
 * registered more than once offers each registration separately), or a
 * single identity result when they share a known frame and need none
 * ({@link bindingsOf}). Only a pair of images that `patient` holds is a
 * candidate, so another patient's dataset never is.
 */
function candidatesFor(
    primaryEntry: DatasetRegistryEntry,
    otherEntry: DatasetRegistryEntry,
    patient: Patient
): BlendCandidate[] {
    if (
        !patient.images.includes(primaryEntry.image) ||
        !patient.images.includes(otherEntry.image)
    ) {
        return [];
    }
    const registrations = patient.findRegistrationsFrom(
        primaryEntry.image,
        otherEntry.image
    );
    return bindingsOf(primaryEntry.image, otherEntry.image, registrations).map(
        (binding) =>
            toBlendCandidate(
                otherEntry,
                binding,
                binding.kind === 'mapped'
                    ? registrations.find(
                          (registration) =>
                              registration.id === binding.registrationId
                      )
                    : undefined
            )
    );
}

/** Whether a candidate's binding is the one a fuse through `registration` (identity when none) needs. */
function bindsThrough(
    binding: ImageBinding,
    registration: RigidRegistration | undefined
): boolean {
    if (!registration) {
        return binding.kind === 'identity';
    }
    return (
        binding.kind === 'mapped' && binding.registrationId === registration.id
    );
}

/**
 * Fuses the dataset `secondarySeriesUid` onto `primarySeriesUid` through
 * `registration` (none for a shared frame of reference), when the same
 * rule that lists a row's candidates admits the pair.
 * Returns whether the fusion was written. The registry is the fusion truth;
 * this is the gate every writer to it goes through.
 */
export function fusePair(
    registry: DatasetEnvironmentRegistry,
    patient: Patient | undefined,
    primarySeriesUid: SeriesInstanceUid,
    secondarySeriesUid: SeriesInstanceUid,
    registration?: RigidRegistration
): boolean {
    const primaryEntry = registry.entryFor(primarySeriesUid);
    const secondaryEntry = registry.entryFor(secondarySeriesUid);
    if (!patient || !primaryEntry || !secondaryEntry) {
        return false;
    }
    const admitted = candidatesFor(primaryEntry, secondaryEntry, patient).some(
        (candidate) => bindsThrough(candidate.binding, registration)
    );
    if (!admitted) {
        return false;
    }
    registry.registerFusion(
        primarySeriesUid,
        secondaryEntry.image,
        registration
    );
    return true;
}

/**
 * The registration `binding` names between the images of `primarySeriesUid`
 * and `otherSeriesUid`, as `patient` holds it; undefined for an identity
 * binding, or when the registration no longer links the two.
 */
export function registrationOfBinding(
    registry: DatasetEnvironmentRegistry,
    patient: Patient | undefined,
    primarySeriesUid: SeriesInstanceUid,
    otherSeriesUid: SeriesInstanceUid,
    binding: ImageBinding
): RigidRegistration | undefined {
    if (binding.kind !== 'mapped' || !patient) {
        return undefined;
    }
    const primaryEntry = registry.entryFor(primarySeriesUid);
    const otherEntry = registry.entryFor(otherSeriesUid);
    if (!primaryEntry || !otherEntry) {
        return undefined;
    }
    return patient
        .findRegistrationsFrom(primaryEntry.image, otherEntry.image)
        .find((registration) => registration.id === binding.registrationId);
}

/**
 * Engine-backed `BlendAdapter`: reads/writes a row's secondary image through
 * the {@link DatasetEnvironmentRegistry}'s fusion slots. Candidate eligibility
 * is the domain's {@link bindingsOf} over the patient's registrations;
 * `Environment.canBePickedAsSecondary` stays defence in
 * depth inside the engine and is never consulted here.
 */
export class RegistryBlendAdapter implements BlendAdapter {
    private readonly sources = new Map<string, BlendSource>();
    private readonly notifiers = new Map<string, ChangeNotifier>();

    /**
     * @param domainChanges The session's committed domain changes. A change
     *     to a registration (made, renamed, approved, rejected, deleted or
     *     undone) changes what a row offers and how its pair reads, so it
     *     reaches the row's sources as a fusion change does.
     */
    public constructor(
        private readonly registry: DatasetEnvironmentRegistry,
        private readonly patient: Patient | undefined,
        private readonly domainChanges?: Pick<DomainStore, 'onChangeset'>
    ) {}

    public sourceFor(primarySeriesUid: SeriesInstanceUid): BlendSource {
        const existing = this.sources.get(primarySeriesUid);
        if (existing) {
            return existing;
        }
        const created = this.createSource(primarySeriesUid);
        this.sources.set(primarySeriesUid, created);
        return created;
    }

    /**
     * Fuses `candidate` onto the row when the gate still admits it. A
     * refused candidate changes nothing and the user is told; with the
     * row's own image gone there is no row to tell it on, so the console is.
     */
    public fuse(
        primarySeriesUid: SeriesInstanceUid,
        candidate: BlendCandidate
    ): void {
        let registration: RigidRegistration | undefined;
        if (candidate.binding.kind === 'mapped') {
            registration = this.resolveRegistration(
                primarySeriesUid,
                candidate
            );
            if (!registration) {
                this.warnRefused(primarySeriesUid, candidate);
                return;
            }
        }
        const fused = fusePair(
            this.registry,
            this.patient,
            primarySeriesUid,
            candidate.seriesInstanceUid,
            registration
        );
        if (!fused) {
            this.warnRefused(primarySeriesUid, candidate);
        }
    }

    public clear(primarySeriesUid: SeriesInstanceUid): void {
        this.registry.clearFusion(primarySeriesUid);
    }

    public setBlend(
        primarySeriesUid: SeriesInstanceUid,
        patch: Partial<BlendSettings>
    ): void {
        this.registry.setBlend(primarySeriesUid, patch);
    }

    /**
     * The row's notifier: it holds the registry's fusion subscription for
     * the row only while someone listens, so a fuse, clear or blend move by
     * any writer reaches the row's sources.
     */
    private notifierFor(primarySeriesUid: SeriesInstanceUid): ChangeNotifier {
        const existing = this.notifiers.get(primarySeriesUid);
        if (existing) {
            return existing;
        }
        let unsubscribe: (() => void) | undefined;
        let stopRegistrationChanges: (() => void) | undefined;
        const notifier = new ChangeNotifier({
            onFirstSubscribe: () => {
                unsubscribe = this.registry.subscribeFusion(
                    primarySeriesUid,
                    () => notifier.notify()
                );
                stopRegistrationChanges = this.domainChanges?.onChangeset(
                    (changes) => {
                        if (
                            changes.some(
                                (change) => change.kind === 'registration'
                            )
                        ) {
                            notifier.notify();
                        }
                    }
                );
            },
            onLastUnsubscribe: () => {
                unsubscribe?.();
                unsubscribe = undefined;
                stopRegistrationChanges?.();
                stopRegistrationChanges = undefined;
            },
        });
        this.notifiers.set(primarySeriesUid, notifier);
        return notifier;
    }

    private createSource(primarySeriesUid: SeriesInstanceUid): BlendSource {
        const notifier = this.notifierFor(primarySeriesUid);
        return {
            subscribe: notifier.subscribe,
            getVersion: notifier.getVersion,
            getCandidates: () => this.candidatesForPrimary(primarySeriesUid),
            getCurrent: () => this.currentFor(primarySeriesUid),
            getBlend: () => this.blendFor(primarySeriesUid),
        };
    }

    private candidatesForPrimary(
        primarySeriesUid: SeriesInstanceUid
    ): ReadonlyArray<BlendCandidate> {
        if (!this.patient) {
            return [];
        }
        const primaryEntry = this.registry.entryFor(primarySeriesUid);
        if (!primaryEntry) {
            return [];
        }
        const others = this.registry
            .listEntries()
            .filter((entry) => entry.seriesUid !== primaryEntry.seriesUid);
        const patient = this.patient;
        return others.flatMap((other) =>
            candidatesFor(primaryEntry, other, patient)
        );
    }

    private currentFor(
        primarySeriesUid: SeriesInstanceUid
    ): BlendCandidate | undefined {
        const primaryEntry = this.registry.entryFor(primarySeriesUid);
        const secondaryImage = primaryEntry?.secondaryImage;
        if (!primaryEntry || !secondaryImage) {
            return undefined;
        }
        const secondaryEntry = this.registry.entryFor(secondaryImage.seriesUid);
        if (!secondaryEntry) {
            return undefined;
        }
        const registration = primaryEntry.registration;
        const [binding] = bindingsOf(
            primaryEntry.image,
            secondaryEntry.image,
            registration ? [registration] : []
        );
        return binding
            ? toBlendCandidate(secondaryEntry, binding, registration)
            : undefined;
    }

    private blendFor(primarySeriesUid: SeriesInstanceUid): BlendSettings {
        return (
            this.registry.entryFor(primarySeriesUid)?.blend ??
            DEFAULT_BLEND_SETTINGS
        );
    }

    private warnRefused(
        primarySeriesUid: SeriesInstanceUid,
        candidate: BlendCandidate
    ): void {
        const primaryEntry = this.registry.entryFor(primarySeriesUid);
        if (!primaryEntry) {
            console.error(
                'Blend: the row image is no longer loaded; the fusion was ' +
                    'not applied.'
            );
            return;
        }
        warnFusionRefused(pairEndOf(primaryEntry.image), candidate);
    }

    /**
     * Re-resolves the domain registration behind a mapped candidate's
     * `registrationId` at fuse time, rather than carrying the domain object
     * across the adapter boundary on the DTO itself.
     */
    private resolveRegistration(
        primarySeriesUid: SeriesInstanceUid,
        candidate: BlendCandidate
    ): RigidRegistration | undefined {
        return registrationOfBinding(
            this.registry,
            this.patient,
            primarySeriesUid,
            candidate.seriesInstanceUid,
            candidate.binding
        );
    }
}
